import * as React from 'react';

import { Drawer } from '@/components/ui';
import { AVAILABLE_IMO_GRADES } from '@/features/user-notifications/utils/constants';

import { useEnrollToIMO } from '../../api/queries';
import { EnrollmentDrawerContent } from '../shared';

/**
 * Props for IMODrawer component
 */
interface IMODrawerProps {
  /** Controls drawer visibility */
  isOpen: boolean;
  /** Callback when drawer is closed */
  onClose: () => void;
}

/**
 * Description text for IMO enrollment drawer
 * Provides information about the course benefits and enrollment call-to-action
 */
const IMO_DESCRIPTION =
  'Prepare for the International Math Olympiad with our specialized course. Sharpen your problem-solving skills and compete globally. Enroll now!';

/**
 * IMO (International Math Olympiad) enrollment drawer component
 *
 * Displays a drawer with enrollment form for IMO course.
 * Users can select their grade and enroll in the course.
 * Uses the shared EnrollmentDrawerContent component for consistent enrollment flow.
 *
 * @param props - Component props
 * @param props.isOpen - Controls drawer visibility
 * @param props.onClose - Callback when drawer is closed
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * <IMODrawer isOpen={isOpen} onClose={() => setIsOpen(false)} />
 * ```
 */
export const IMODrawer: React.FC<IMODrawerProps> = ({ isOpen, onClose }) => {
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      contentClassName="bg-magical-moonlight-white"
      childClass="!max-w-full"
      topOffset="top-[25%]"
      closeButtonPosition={{ top: 'top-[calc(25%-45px)]', right: 'right-6' }}
      innerContentClassName="p-2 md:p-3"
      role="dialog"
      aria-modal="true"
    >
      <EnrollmentDrawerContent
        description={IMO_DESCRIPTION}
        availableGrades={AVAILABLE_IMO_GRADES}
        useEnrollmentHook={useEnrollToIMO}
        onClose={onClose}
        enrollmentType="imo"
      />
    </Drawer>
  );
};
